import { defineStore } from "pinia";
import { useQuotesStore } from "./quotes";
import { generateCacheKey, clearCache } from "@/composables/useCache";
import { store } from "@/stores/global.js";
import { doFetch } from "@/composables/doFetch";

export const usePeersStore = defineStore("peers", {
    state: () => ({
        peersBySymbol: {}, // Store peer relationships by symbol and parameters
        peerData: {}, // Store the complete peer data
        isLoading: false,
        lastFetchTime: {}, // Track last fetch time by cache key
        fetchInProgress: {}, // Track ongoing fetch requests
        cacheTimeMs: 300000, // 5 minutes cache
    }),

    getters: {
        /**
         * Get all peers for a specific symbol
         */
        getPeers:
            state =>
            (symbol, limit = 10, naics = false, securityType = "CS") => {
                if (!symbol) return [];
                const cacheKey = generateCacheKey(symbol, limit, naics, securityType);
                return state.peersBySymbol[cacheKey] || [];
            },

        /**
         * Get all peer data for a specific symbol
         */
        getPeerData:
            state =>
            (symbol, limit = 10, naics = false, securityType = "CS") => {
                if (!symbol) return [];
                const cacheKey = generateCacheKey(symbol, limit, naics, securityType);
                return state.peerData[cacheKey] || [];
            },
    },
    actions: {
        /**
         * Direct API call to fetch peers data
         * @param {string} symbol - Symbol to fetch peers for
         * @param {number} limit - Maximum number of peers to fetch
         * @param {boolean} naics - Whether to use NAICS for peer lookup
         * @param {string} securityType - Type of security to filter for
         * @param {string} token - Authentication token
         * @returns {Promise<Object>} - API response
         */
        async callPeersAPI(symbol, limit, naics, securityType, token) {
            // Choose the correct API endpoint based on naics parameter
            const apiEndpoint = naics ? "getIndustrySectorPeers" : "getGlobalIndustrySectorPeers";
            const url = new URL(`https://${store.env}.quotemedia.com/datatool/${apiEndpoint}.json`);

            url.searchParams.append("webmaster_id", store.wmid);
            url.searchParams.append("webmasterId", store.wmid);
            url.searchParams.append("qmodTool", "MiniQuotes"); // Use component name as tool
            url.searchParams.append("pathName", window.location.pathname);
            if (store.sid) url.searchParams.append("sid", store.sid);

            // Use peerSymbol parameter name to match DATA$farm pattern
            url.searchParams.append("peerSymbol", symbol);
            url.searchParams.append("limit", limit.toString());
            if (securityType) url.searchParams.append("securityType", securityType);

            return await doFetch(url, {
                method: "GET",
                headers: {
                    "Accept-Language": "en",
                    "Content-Type": "application/json",
                    "Datatool-Token": token,
                },
            });
        } /**
         * Fetch peers for the provided symbol
         * @param {string} symbol - Symbol to fetch peers for
         * @param {string} token - Authentication token for the API call
         * @param {number} limit - Maximum number of peers to fetch
         * @param {boolean} naics - Whether to use NAICS for peer lookup
         * @param {string} securityType - Type of security to filter for
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Array>} - Array of peer symbols
         */,
        async fetchPeers(
            symbol,
            token,
            limit = 10,
            naics = false,
            securityType = "CS",
            force = false
        ) {
            if (!symbol) return [];
            if (!token) throw new Error("Token is required for API calls");

            // Normalize symbol
            const normalizedSymbol = symbol.trim().toUpperCase();

            // Generate a cache key that includes all request parameters
            const cacheKey = generateCacheKey(normalizedSymbol, limit, naics, securityType);

            // If already fetching this data with these parameters, wait for that request
            if (this.fetchInProgress[cacheKey]) {
                return this.fetchInProgress[cacheKey];
            }

            // Check if we need to fetch (not cached or force refresh)
            const now = Date.now();
            const needsFetch =
                force ||
                !this.peersBySymbol[cacheKey] ||
                !this.lastFetchTime[cacheKey] ||
                now - this.lastFetchTime[cacheKey] > this.cacheTimeMs;

            if (!needsFetch) {
                return this.peersBySymbol[cacheKey];
            }

            const fetchPromise = (async () => {
                try {
                    this.isLoading = true;

                    const response = await this.callPeersAPI(
                        normalizedSymbol,
                        limit,
                        naics,
                        securityType,
                        token
                    );

                    // Handle error response from doFetch
                    if (response.error) {
                        throw new Error(`API call failed: ${response.error}`);
                    }

                    let peerData = [];
                    let peerSymbols = [];

                    // Process the response based on the peer.json format
                    if (response && response.results && response.results.peer) {
                        // Store the full peer data
                        peerData = response.results.peer.map(peer => ({
                            symbol: peer.symbolinfo?.symbolstring|| "",
                            longName: peer.symbolinfo?.equityinfo?.longname || "",
                            shortName: peer.symbolinfo?.equityinfo?.shortname || "",
                            exchange: peer.symbolinfo?.key?.exchange || "",
                            last: peer.pricedata?.last || 0,
                            change: peer.pricedata?.change || 0,
                            changePercent: peer.pricedata?.changepercent || 0,
                            volume: peer.pricedata?.sharevolume || 0,
                            marketCap: peer.fundamental?.marketcap || 0,
                            // Add more fields as needed
                            rawData: peer, // Keep the original data
                        }));

                        // Extract just the symbols for compatibility with existing code
                        peerSymbols = peerData.map(peer => peer.symbol).filter(Boolean);
                    }

                    // Update the store with new data using the specific cache key
                    this.peerData[cacheKey] = peerData;
                    this.peersBySymbol[cacheKey] = peerSymbols;
                    this.lastFetchTime[cacheKey] = now;

                    return peerSymbols;
                } finally {
                    this.isLoading = false;
                    delete this.fetchInProgress[cacheKey];
                }
            })();

            this.fetchInProgress[cacheKey] = fetchPromise;
            return fetchPromise;
        },

        /**
         * Format peer data for display from the peer API response
         * @param {Array} peerData - Raw peer data
         * @returns {Array} - Formatted peer data for display
         */
        formatPeerData(peerData) {
            return peerData.map(peer => {
                const isPositive = (peer.change || 0) >= 0;

                return {
                    symbol: peer.symbol,
                    longName: peer.longName,
                    last: peer.last || 0,
                    change: peer.change || 0,
                    changePercent: peer.changePercent || 0,
                    changeDirection: isPositive ? "positive" : "negative",
                    changeSign: isPositive ? "+" : "",
                    volume: peer.volume || 0,
                    marketCap: peer.marketCap || 0,
                };
            });
        } /**
         * Fetch peers and their quote data
         * @param {string} symbol - Symbol to fetch peers for
         * @param {string} token - Authentication token for the API call
         * @param {number} limit - Maximum number of peers
         * @param {boolean} naics - Whether to use NAICS for peer lookup
         * @param {string} securityType - Type of security to filter for
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Array>} - Array of formatted quote data for peers
         */,
        async fetchPeersWithQuotes(
            symbol,
            token,
            limit = 10,
            naics = false,
            securityType = "CS",
            force = false
        ) {
            if (!symbol) return [];

            // Normalize symbol
            const normalizedSymbol = symbol.trim().toUpperCase();

            // Generate a cache key that includes all request parameters
            const cacheKey = generateCacheKey(normalizedSymbol, limit, naics, securityType);

            // Fetch peers and their data
            await this.fetchPeers(normalizedSymbol, token, limit, naics, securityType, force);

            const peerData = this.peerData[cacheKey];
            if (!peerData || peerData.length === 0) {
                return [];
            }

            // Format the peer data for display (similar to what the quotes store does)
            return this.formatPeerData(peerData);
        },

        /**
         * Clear cached data for specific parameters or all data
         * @param {string|null} symbol - Symbol to clear or null for all
         * @param {number|null} limit - Limit to clear or null for all
         * @param {boolean|null} naics - NAICS setting to clear or null for all
         * @param {string|null} securityType - Security type to clear or null for all
         */
        clearCache(symbol = null, limit = null, naics = null, securityType = null) {
            // Create filters object based on parameters
            const filters = {};
            if (symbol !== null) filters[0] = symbol;
            if (limit !== null) filters[1] = String(limit);
            if (naics !== null) filters[2] = String(naics);
            if (securityType !== null) filters[3] = securityType;

            // Clear both the peer data and peer symbols caches
            clearCache(
                {
                    dataCache: this.peerData,
                    timeCache: this.lastFetchTime,
                },
                {
                    separator: "|",
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );

            clearCache(
                {
                    dataCache: this.peersBySymbol,
                    timeCache: {}, // No time cache for this one
                },
                {
                    separator: "|",
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );
        },
    },
});
